"use client";

import { type ReactNode } from "react";
import { cn } from "./cn";

export interface TabItem<T extends string = string> {
  value: T;
  label: ReactNode;
}

export interface TabsProps<T extends string = string> {
  items: TabItem<T>[];
  value: T;
  onValueChange: (value: T) => void;
  className?: string;
  /**
   * `tablist` (default) when the value picks which panel is shown;
   * `radiogroup` when it picks a setting — a preference page's "Lys / Mørk /
   * System" is a choice, not navigation, and a screen reader should say so.
   */
  role?: "tablist" | "radiogroup";
}

/** Segmented control — a controlled row of pill tabs in a muted track. */
export function Tabs<T extends string = string>({
  items,
  value,
  onValueChange,
  className,
  role = "tablist",
}: TabsProps<T>) {
  const radio = role === "radiogroup";
  return (
    <div
      role={role}
      className={cn(
        "inline-flex items-center gap-1 p-1 rounded-control bg-muted",
        className,
      )}
    >
      {items.map((item) => {
        const active = item.value === value;
        return (
          <button
            key={item.value}
            type="button"
            role={radio ? "radio" : "tab"}
            aria-selected={radio ? undefined : active}
            aria-checked={radio ? active : undefined}
            onClick={() => onValueChange(item.value)}
            className={cn(
              "h-7 px-3 rounded-md text-sm font-medium transition-colors",
              active
                ? "bg-surface text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
